import type { CSSProperties, ReactNode } from 'react'
import { Modal } from './Modal'

// ============================================
// Types
// ============================================

interface KeyboardShortcutsHelpProps {
  isOpen: boolean
  onClose: () => void
}

interface ShortcutItemProps {
  keys: string[]
  description: string
}

interface ShortcutGroup {
  title: string
  shortcuts: ShortcutItemProps[]
}

// ============================================
// Styles
// ============================================

const containerStyles: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
}

const groupStyles: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
}

const groupTitleStyles: CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: '11px',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  color: 'var(--color-ink-tertiary)',
  margin: 0,
}

const shortcutRowStyles: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px 0',
  borderBottom: 'var(--border-subtle)',
}

const shortcutDescriptionStyles: CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: '14px',
  color: 'var(--color-ink)',
}

const keysContainerStyles: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
}

const kbdStyles: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '24px',
  height: '24px',
  fontFamily: 'var(--font-body)',
  fontSize: '12px',
  fontWeight: 600,
  color: 'var(--color-ink-subtle)',
  backgroundColor: 'var(--color-surface)',
  border: 'var(--border-light)',
  borderRadius: '6px',
  padding: '0 6px',
  boxShadow: '0 1px 2px rgba(44, 36, 22, 0.05)',
}

const plusStyles: CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: '10px',
  color: 'var(--color-ink-tertiary)',
}

// ============================================
// Data
// ============================================

const shortcutGroups: ShortcutGroup[] = [
  {
    title: 'Global',
    shortcuts: [
      { keys: ['Cmd', 'K'], description: 'Open search' },
      { keys: ['Cmd', '.'], description: 'Toggle sidebar' },
      { keys: ['Cmd', ','], description: 'Open settings' },
      { keys: ['Esc'], description: 'Exit / Close' },
    ],
  },
  {
    title: 'Create',
    shortcuts: [{ keys: ['Cmd', 'Enter'], description: 'Create card' }],
  },
  {
    title: 'Review',
    shortcuts: [
      { keys: ['1'], description: 'Rate: Again' },
      { keys: ['2'], description: 'Rate: Hard' },
      { keys: ['3'], description: 'Rate: Good' },
      { keys: ['Space'], description: 'Reveal card' },
    ],
  },
  {
    title: 'Navigation',
    shortcuts: [
      { keys: ['J'], description: 'Move down in list' },
      { keys: ['K'], description: 'Move up in list' },
      { keys: ['Enter'], description: 'Select item' },
    ],
  },
]

// ============================================
// Components
// ============================================

function ShortcutItem({ keys, description }: ShortcutItemProps): ReactNode {
  return (
    <div style={shortcutRowStyles}>
      <span style={shortcutDescriptionStyles}>{description}</span>
      <div style={keysContainerStyles}>
        {keys.map((key, index) => (
          <span key={key} style={{ display: 'contents' }}>
            {index > 0 && <span style={plusStyles}>+</span>}
            <kbd style={kbdStyles}>{key}</kbd>
          </span>
        ))}
      </div>
    </div>
  )
}

export function KeyboardShortcutsHelp({ isOpen, onClose }: KeyboardShortcutsHelpProps): ReactNode {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Keyboard Shortcuts">
      <div style={containerStyles}>
        {shortcutGroups.map((group) => (
          <div key={group.title} style={groupStyles}>
            <h3 style={groupTitleStyles}>{group.title}</h3>
            {group.shortcuts.map((shortcut) => (
              <ShortcutItem
                key={shortcut.description}
                keys={shortcut.keys}
                description={shortcut.description}
              />
            ))}
          </div>
        ))}
      </div>
    </Modal>
  )
}
